// Import necessary React hooks
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// Import axios utilities
import axios from "axios";
// Import necessary Material-UI components for styling and layout
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Typography,
} from "@mui/material";
// Import lodash for utility functions
import _ from "lodash";
// Import custom components for displaying item details, approval details, and images
import {
  ApprovalDetails,
  ViewImage,
  ItemListTableItemDetails,
  apiURI,
  authErrorRedirect,
  Loader,
  CustomMUIChip,
} from "../../../../common";
import {
  SupplierDetails,
  SupplierDetailsInactiveItem,
} from "../../../../sampleControl";

/**
 * ItemApprovals component for displaying all details of the item in a dialog
 * @param {Object} props - Component props including modal state, data, and callbacks.
 */
export function AllItemsItemDetailsDialog({ rowData, open, handleClose }) {
  // Navigation hook from React Router
  const history = useNavigate();
  // State for managing loaded item data
  const [itemDetails, setItemDetails] = useState();
  // State for managing errors
  const [errors, setErrors] = useState({});
  // State for controlling form reload
  const [reloadData, setReloadData] = useState(false);

  useEffect(() => {
    // Clear any previous item details
    setItemDetails();
    // Start loading state
    setReloadData(false);
    axios
      .get(`${apiURI}/form/getItem/${rowData.sampleItemID}`)
      .then((response) => {
        // Set item details when response is returned
        setItemDetails(response.data);
      })
      .catch((error) => {
        console.log("error in axios form call react");
        // Clear errors
        setErrors({});
        // Set errors received from server response
        setErrors(error.response?.data);
        // Redirect on authentication error
        authErrorRedirect(error.response?.data, history);
      });
  }, [rowData.sampleItemID, reloadData]);

  // Log errors to console if any
  errors && console.log(errors, "errors");
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      sx={{ height: "90%" }}
    >
      <DialogTitle>
        <span style={{ paddingRight: "20px" }}> Item Details </span>
        {itemDetails && (
          <CustomMUIChip
            label={itemDetails?.isActive === true ? "Active" : "Inactive"}
            status={itemDetails?.isActive}
            variant="outlined"
            color={itemDetails?.isActive === true ? "success" : "error"}
          />
        )}
      </DialogTitle>
      {itemDetails ? (
        <DialogContent>
          <Box>
            {/* Display item details, excluding certain fields */}
            <ItemListTableItemDetails
              cellValues={_.omit(itemDetails, [
                "requesterSampleImage",
                "procurementApproval",
                "supplierList",
                "id",
                "isActive",
                "_id",
              ])}
            />
            {/* Display reference image(s) if available */}
            {itemDetails.requesterSampleImage && (
              <Grid
                container
                style={{
                  fontSize: "14px",
                  marginBottom: "15px",
                }}
              >
                <Grid item xs={12} sm={5}>
                  <span>Reference Image(s):</span>
                </Grid>
                <Grid item xs={12} sm={7}>
                  {Array.isArray(itemDetails.requesterSampleImage) ? (
                    itemDetails.requesterSampleImage.map((image, index) => {
                      return (
                        <ViewImage
                          productImage={image}
                          key={index}
                          altIndex={index}
                        />
                      );
                    })
                  ) : (
                    <ViewImage
                      productImage={itemDetails.requesterSampleImage}
                    />
                  )}
                </Grid>
              </Grid>
            )}
            {/* Display supplier details if available */}
            {itemDetails.supplierList &&
              (itemDetails.isActive ? (
                <SupplierDetails
                  supplierValues={itemDetails.supplierList}
                  sampleRequestID={itemDetails.sampleRequestID}
                  sampleItemID={itemDetails.sampleItemID}
                  setReloadData={setReloadData}
                />
              ) : (
                <SupplierDetailsInactiveItem
                  supplierValues={itemDetails.supplierList}
                  sampleRequestID={itemDetails.sampleRequestID}
                  sampleItemID={itemDetails.sampleItemID}
                  setReloadData={setReloadData}
                />
              ))}
            {/* Display user approval details if available */}
            {itemDetails.userApproval && (
              <Grid item xs={12} sm={12} mt={2}>
                <Typography
                  style={{
                    fontSize: "16px",
                    marginBottom: "15px",
                    marginTop: "10px",
                  }}
                  gutterBottom
                >
                  <b>Chef Approval:</b>
                </Typography>

                <ApprovalDetails data={itemDetails.userApproval} />
              </Grid>
            )}
            {/* Display executive chef approval details if available */}
            {itemDetails.execChefApproval && (
              <Grid item xs={12} sm={12} mt={2}>
                <span>
                  <b>Executive Chef Approval:</b>
                </span>
                <ApprovalDetails data={itemDetails.execChefApproval} />
              </Grid>
            )}
            {/* Display HOD approval details if available */}
            {itemDetails.HODApproval && (
              <Grid item xs={12} sm={12} mt={2}>
                <span>
                  <b>HOD Approval:</b>
                </span>
                <ApprovalDetails data={itemDetails.HODApproval} />
              </Grid>
            )}
          </Box>
        </DialogContent>
      ) : (
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Loader />
        </Grid>
      )}

      <DialogActions>
        {/* Button to close the dialog */}
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
